import ReactMarkdown, { type Components } from 'react-markdown';
import './MarkdownContent.scss';

interface MarkdownContentProps {
  content?: string | null;
  className?: string;
  /**
   * Untrusted-author mode (e.g. Relations discussion bodies): only http(s)
   * and mailto hrefs stay links, and they open in a new tab with
   * rel="noopener noreferrer"; anything else renders as plain text. Images
   * are never rendered (an `img` would load a remote URL with no vetting);
   * only the alt text is shown, as plain text. Raw HTML is never rendered in
   * either mode (no rehype-raw).
   */
  safeLinks?: boolean;
}

const SAFE_HREF = /^(https?:|mailto:)/i;

const SafeLink: Components['a'] = ({ href, children }) => {
  if (!href || !SAFE_HREF.test(href)) return <span>{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const SafeImage: Components['img'] = ({ alt }) => <span>{alt}</span>;

const SAFE_COMPONENTS: Components = { a: SafeLink, img: SafeImage };

const MarkdownContent = ({ content, className = '', safeLinks = false }: MarkdownContentProps) => {
  // Defensive: a stale cached bundle may be handed a RichDoc object where this
  // component expects legacy markdown; degrade to blank rather than crash.
  if (typeof content !== 'string') return null;
  if (!content) return null;

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown components={safeLinks ? SAFE_COMPONENTS : undefined}>{content}</ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
